import {
    Alert,
    Autocomplete,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';

/**
 * A small form dialog driven by a field list, used for every task action that needs input
 * (block, flag, send back, reassign, hold, cancel, comment).
 *
 * fields: [{ name, label, type: 'text' | 'select' | 'radio' | 'user' | 'users' | 'checkbox' | 'date' | 'heading', required,
 *            multiline, options: [{ value, label }], help, initial, users, hidden: (values) => bool,
 *            validate: (value) => error message or null, placeholder }]
 * A heading is a label between groups of fields, with help as a line under it.
 * usageName is the action's row in Feature Usage. Titles can't be: some carry a stage name or a count.
 */
const TaskActionDialog = ({ open, usageName = 'TaskActionDialog', title, description, fields, submitLabel = 'Save', submitColor = 'primary', onSubmit, onClose }) => {
    useTrackUsage(usageName, 'dialog', 'tasks', open);
    const [values, setValues] = useState({});
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            const initial = {};
            (fields || []).forEach((f) => {
                if (f.type === 'heading') return;
                initial[f.name] = f.initial ?? (f.type === 'checkbox' ? false : f.type === 'users' ? [] : '');
            });
            setValues(initial);
            setError(null);
            setSaving(false);
        }
    }, [open, fields]);

    const set = (name, value) => setValues((v) => ({ ...v, [name]: value }));
    const shown = (fields || []).filter((f) => !(f.hidden && f.hidden(values)));
    const missing = shown.filter(
        (f) => f.required && (values[f.name] === '' || values[f.name] == null || values[f.name] === false),
    );
    const invalid = shown.filter((f) => f.validate && f.validate(values[f.name]));

    const submit = async () => {
        setSaving(true);
        setError(null);
        try {
            await onSubmit(values);
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || 'Something went wrong');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth disableEnforceFocus>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {description}
                    </Typography>
                )}
                {(fields || []).map((f) => {
                    if (f.hidden && f.hidden(values)) return null;
                    if (f.type === 'heading') {
                        return (
                            <Typography key={f.name || f.label} variant="subtitle2" sx={{ mt: 2 }}>
                                {f.label}
                                {f.help && (
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {f.help}
                                    </Typography>
                                )}
                            </Typography>
                        );
                    }
                    if (f.type === 'checkbox') {
                        return (
                            <FormControlLabel
                                key={f.name}
                                sx={{ display: 'flex', mt: 1 }}
                                control={<Checkbox checked={!!values[f.name]} onChange={(e) => set(f.name, e.target.checked)} />}
                                label={f.label}
                            />
                        );
                    }
                    if (f.type === 'radio') {
                        return (
                            <FormControl key={f.name} sx={{ display: 'flex', mt: 1.5 }}>
                                <FormLabel id={`${f.name}-label`} sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {typeof f.label === 'function' ? f.label(values) : f.label}
                                </FormLabel>
                                <RadioGroup
                                    aria-labelledby={`${f.name}-label`}
                                    value={values[f.name] ?? ''}
                                    onChange={(e) => set(f.name, e.target.value)}
                                >
                                    {(f.options || []).map((o) => (
                                        <FormControlLabel
                                            key={o.value}
                                            value={o.value}
                                            control={<Radio size="small" />}
                                            label={
                                                <Typography variant="body2">
                                                    {typeof o.label === 'function' ? o.label(values) : o.label}
                                                </Typography>
                                            }
                                            sx={{ alignItems: 'flex-start', mt: 0.5, '& .MuiRadio-root': { pt: 0.25 } }}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        );
                    }
                    if (f.type === 'users') {
                        const users = f.users || [];
                        const selected = users.filter((u) => (values[f.name] || []).includes(u.email));
                        return (
                            <Autocomplete
                                key={f.name}
                                multiple
                                options={users}
                                value={selected}
                                getOptionLabel={(u) => (u.name && u.name !== u.email ? `${u.name} (${u.email})` : u.email)}
                                isOptionEqualToValue={(a, b) => a.email === b.email}
                                onChange={(_, list) => set(f.name, list.map((u) => u.email))}
                                renderInput={(params) => <TextField {...params} margin="dense" label={f.label} helperText={f.help} />}
                            />
                        );
                    }
                    if (f.type === 'user') {
                        const users = f.users || [];
                        const selected = users.find((u) => u.email === values[f.name]) || null;
                        return (
                            <Autocomplete
                                key={f.name}
                                options={users}
                                value={selected}
                                getOptionLabel={(u) => (u.name && u.name !== u.email ? `${u.name} (${u.email})` : u.email)}
                                isOptionEqualToValue={(a, b) => a.email === b.email}
                                onChange={(_, u) => set(f.name, u ? u.email : '')}
                                renderInput={(params) => (
                                    <TextField {...params} margin="dense" label={f.label} required={f.required} helperText={f.help} />
                                )}
                            />
                        );
                    }
                    const fieldError = f.validate ? f.validate(values[f.name]) : null;
                    return (
                        <TextField
                            key={f.name}
                            select={f.type === 'select'}
                            type={f.type === 'date' ? 'date' : 'text'}
                            InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
                            fullWidth
                            margin="dense"
                            label={f.label}
                            required={f.required}
                            multiline={!!f.multiline}
                            minRows={f.multiline ? 2 : undefined}
                            value={values[f.name] ?? ''}
                            onChange={(e) => set(f.name, e.target.value)}
                            placeholder={f.placeholder}
                            error={!!fieldError}
                            helperText={fieldError || f.help}
                        >
                            {f.type === 'select' &&
                                (f.options || []).map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                        </TextField>
                    );
                })}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" color={submitColor} onClick={submit} disabled={saving || missing.length > 0 || invalid.length > 0}>
                    {submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaskActionDialog;
